import { useEffect, useMemo, useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { startOfMonth } from "date-fns";
import { useTranslation } from "@/hooks/useTranslation";
import { Trash2 } from "lucide-react";
import { FullscreenFormDialog } from "@/components/ui/fullscreen-form-dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { DatePicker } from "@/components/ui/date-picker";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useOrganization } from "@/contexts/OrganizationContext";
import { useCreateBudget, useUpdateBudget, useDeleteBudget } from "@/hooks/budgets/useEnvelopeBudgets";
import { useFunds } from "@/hooks/useFunds";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { formatLocalISODate, parseLocalISODate } from "@/lib/dateUtils";
import { EnvelopeBudget } from "@/types/budgets";
import { useOrgNavigate } from "@/hooks/useOrgNavigate";
import { BUDGET_STATUSES, BUDGET_TYPES } from "@releve/shared";

const createFormSchema = (t: (key: string) => string) => z.object({
  name: z.string().min(1, t('budgets.validation.nameRequired')),
  description: z.string().optional(),
  status: z.enum(BUDGET_STATUSES).optional(),
  budget_type: z.enum(BUDGET_TYPES),
  scope: z.enum(["organization", "project"]),
  project_id: z.string().optional().nullable(),
  fund_id: z.string().optional().nullable(),
  start_month: z.date({ required_error: t('budgets.validation.startMonthRequired') }),
  end_month: z.date().optional().nullable(),
}).refine((data) => {
  if (data.end_month && data.start_month) {
    return data.end_month >= data.start_month;
  }
  return true;
}, { message: t('budgets.validation.endMonthAfterStart'), path: ["end_month"] })
.refine((data) => {
  if (data.budget_type === 'total' && !data.end_month) {
    return false;
  }
  return true;
}, { message: t('budgets.endMonthRequiredForTotal'), path: ["end_month"] });

type FormValues = z.infer<ReturnType<typeof createFormSchema>>;

interface CreateBudgetDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: (budgetId: string) => void;
  budget?: EnvelopeBudget;
  onComplete?: () => void;
  /**
   * Preselects the fund of a NEW budget (the fund detail page's "Create
   * budget"). Ignored when editing — an existing budget keeps its own fund.
   */
  defaultFundId?: string | null;
}

export function CreateBudgetDialog({ open, onOpenChange, onCreated, budget, onComplete, defaultFundId }: CreateBudgetDialogProps) {
  const { t } = useTranslation();
  const { selectedOrganization } = useOrganization();
  const orgNavigate = useOrgNavigate();
  const createBudget = useCreateBudget();
  const updateBudget = useUpdateBudget();
  const deleteBudget = useDeleteBudget();
  const orgId = selectedOrganization?.id;
  const isEditing = !!budget;
  const formSchema = useMemo(() => createFormSchema(t), [t]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      description: "",
      status: "active",
      budget_type: "envelope",
      scope: "organization",
      project_id: null,
      fund_id: defaultFundId ?? null,
      start_month: startOfMonth(new Date()),
      end_month: null,
    },
  });

  useEffect(() => {
    if (budget && open) {
      form.reset({
        name: budget.name,
        description: budget.description ?? "",
        status: budget.status,
        budget_type: budget.budget_type ?? "envelope",
        scope: budget.project_id ? "project" : "organization",
        project_id: budget.project_id,
        fund_id: budget.fund_id,
        start_month: parseLocalISODate(budget.start_month) ?? startOfMonth(new Date()),
        end_month: budget.end_month ? parseLocalISODate(budget.end_month) : null,
      });
    } else if (!budget && open) {
      form.reset({
        name: "",
        description: "",
        status: "active",
        budget_type: "envelope",
        scope: "organization",
        project_id: null,
        fund_id: defaultFundId ?? null,
        start_month: startOfMonth(new Date()),
        end_month: null,
      });
    }
  }, [budget, open, form, defaultFundId]);

  const budgetType = form.watch("budget_type");
  const scope = form.watch("scope");

  const { data: projects } = useQuery({
    queryKey: ['projects', orgId],
    queryFn: async () => {
      if (!orgId) return [];
      const { data, error } = await supabase
        .from('projects')
        .select('id, name, code')
        .eq('organization_id', orgId)
        .order('name');
      if (error) throw error;
      return data as { id: string; name: string; code: string | null }[];
    },
    enabled: !!orgId && scope === 'project',
  });

  // Every fund, pipeline included — a budget may plan a fund not yet
  // awarded (unlike the posting pickers, which offer awarded funds only).
  const { data: fundRows } = useFunds(orgId);
  const funds = useMemo(
    () => [...(fundRows ?? [])].sort((a, b) => a.fund_name.localeCompare(b.fund_name)),
    [fundRows],
  );

  const isPending = isEditing ? updateBudget.isPending : createBudget.isPending;

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    form.handleSubmit((values: FormValues) => {
      if (!orgId) return;

      const normalizedStartMonth = startOfMonth(values.start_month);
      const normalizedEndMonth = values.end_month ? startOfMonth(values.end_month) : null;

      if (isEditing && budget) {
        updateBudget.mutate(
          {
            id: budget.id,
            data: {
              name: values.name,
              description: values.description || null,
              status: values.status,
              fund_id: values.fund_id || null,
              start_month: formatLocalISODate(normalizedStartMonth),
              end_month: normalizedEndMonth ? formatLocalISODate(normalizedEndMonth) : null,
            },
          },
          {
            onSuccess: () => {
              onOpenChange(false);
              onComplete?.();
            },
          }
        );
      } else {
        createBudget.mutate(
          {
            organizationId: orgId,
            data: {
              name: values.name,
              description: values.description || undefined,
              status: values.status,
              budget_type: values.budget_type,
              project_id: values.scope === 'project' ? values.project_id : null,
              fund_id: values.fund_id || null,
              start_month: formatLocalISODate(normalizedStartMonth),
              end_month: normalizedEndMonth ? formatLocalISODate(normalizedEndMonth) : null,
            },
          },
          {
            onSuccess: (result) => {
              form.reset();
              // Continue to the new budget's detail page. Navigate with `replace` so it
              // supersedes the dialog's history entry — FullscreenFormDialog then sees its
              // entry is gone and skips its own history.back() on close (no race, no orphan).
              // Navigate first, then close.
              orgNavigate(`/accounting/budgets/${result.id}`, { replace: true });
              onOpenChange(false);
              onCreated?.(result.id);
            },
          }
        );
      }
    })();
  };

  const handleDeleteBudget = useCallback(() => {
    if (!budget || !orgId) return;
    deleteBudget.mutate(
      { id: budget.id, organizationId: orgId },
      {
        onSuccess: () => {
          setDeleteDialogOpen(false);
          onOpenChange(false);
          orgNavigate('/accounting/budgets');
        },
      }
    );
  }, [budget, orgId, deleteBudget, orgNavigate, onOpenChange]);

  return (
    <>
      <FullscreenFormDialog
        open={open}
        onOpenChange={onOpenChange}
        title={isEditing ? t('budgets.editBudget') : t('budgets.createBudget')}
        onSubmit={onSubmit}
        submitLabel={isEditing ? t('budgets.saveBudget') : t('budgets.createBudget')}
        submittingLabel={isEditing ? t('saving') : t('budgets.creating')}
        isSubmitting={isPending}
        skipCloseConfirmation={!form.formState.isDirty}
        formId="budget-settings-form"
        description={isEditing ? t('budgets.editBudgetDescription') : t('budgets.createBudgetDescription')}
      >
        <Form {...form}>
          <div className="space-y-8">
            {/* Budget content card — wraps the document body */}
            <Card>
              <CardContent className="pt-6 space-y-8">
                <h2 className="text-[40px] font-extralight leading-tight text-right mb-3 uppercase">
                  {t('budgets.budget')}
                </h2>

                {/* SECTION: Basic Information */}
                <div>
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                    {t('budgets.sections.basicInfo')}
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="name"
                      render={({ field, fieldState }) => (
                        <FormItem>
                          <FormLabel>
                            {t('name')}
                            {fieldState.error && (
                              <span className="text-destructive ml-2 text-xs">{fieldState.error.message}</span>
                            )}
                          </FormLabel>
                          <FormControl>
                            <Input {...field} placeholder={t('budgets.namePlaceholder')} />
                          </FormControl>
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="status"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('budgets.statusLabel')}</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="active">{t('budgets.status.active')}</SelectItem>
                              <SelectItem value="closed">{t('budgets.status.closed')}</SelectItem>
                              <SelectItem value="archived">{t('budgets.status.archived')}</SelectItem>
                            </SelectContent>
                          </Select>
                        </FormItem>
                      )}
                    />

                    <div className="col-span-2">
                      <FormField
                        control={form.control}
                        name="description"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t('description')}</FormLabel>
                            <FormControl>
                              <Textarea {...field} rows={2} />
                            </FormControl>
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>
                </div>

                {/* Full-bleed colored section: Type & Scope (the defining configuration).
                    Uses theme-aware `bg-muted` (not the invoice's frozen `bg-releve-gray-light`)
                    because the selects/labels render theme-aware text. */}
                <div className="-mx-6 px-6 py-6 bg-muted">
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                    {t('budgets.sections.typeAndScope')}
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="budget_type"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('budgets.budgetType')}</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value} disabled={isEditing}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="envelope">{t('budgets.budgetTypeEnvelope')}</SelectItem>
                              <SelectItem value="total">{t('budgets.budgetTypeTotal')}</SelectItem>
                            </SelectContent>
                          </Select>
                          <p className="text-xs text-muted-foreground mt-1">
                            {field.value === 'envelope'
                              ? t('budgets.budgetTypeEnvelopeDescription')
                              : t('budgets.budgetTypeTotalDescription')}
                          </p>
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="scope"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('budgets.scope')}</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value} disabled={isEditing}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="organization">{t('budgets.scopeOrganization')}</SelectItem>
                              <SelectItem value="project">{t('budgets.scopeProject')}</SelectItem>
                            </SelectContent>
                          </Select>
                        </FormItem>
                      )}
                    />

                    {scope === 'project' && (
                      <FormField
                        control={form.control}
                        name="project_id"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t('project')}</FormLabel>
                            <Select onValueChange={field.onChange} value={field.value ?? undefined}>
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder={t('budgets.selectProject')} />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {projects?.map((p) => (
                                  <SelectItem key={p.id} value={p.id}>
                                    {p.code ? `${p.code} - ${p.name}` : p.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </FormItem>
                        )}
                      />
                    )}

                    {funds.length > 0 && (
                      <FormField
                        control={form.control}
                        name="fund_id"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t('budgets.fund')}</FormLabel>
                            <Select
                              onValueChange={(v) => field.onChange(v === '__none__' ? null : v)}
                              value={field.value ?? '__none__'}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder={t('budgets.allFunds')} />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="__none__">{t('budgets.allFunds')}</SelectItem>
                                {funds.map((f) => (
                                  <SelectItem key={f.id} value={f.id}>{f.fund_name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </FormItem>
                        )}
                      />
                    )}
                  </div>
                </div>

                {/* SECTION: Period */}
                <div>
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                    {t('budgets.sections.period')}
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="start_month"
                      render={({ field, fieldState }) => (
                        <FormItem>
                          <FormLabel>
                            {t('budgets.startMonth')}
                            {fieldState.error && (
                              <span className="text-destructive ml-2 text-xs">{fieldState.error.message}</span>
                            )}
                          </FormLabel>
                          <FormControl>
                            <DatePicker
                              value={field.value}
                              onChange={(date) => field.onChange(date ? startOfMonth(date) : undefined)}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="end_month"
                      render={({ field, fieldState }) => (
                        <FormItem>
                          <FormLabel>
                            {t('budgets.endMonth')}
                            {fieldState.error && (
                              <span className="text-destructive ml-2 text-xs">{fieldState.error.message}</span>
                            )}
                          </FormLabel>
                          <FormControl>
                            <DatePicker
                              value={field.value ?? undefined}
                              onChange={(date) => field.onChange(date ? startOfMonth(date) : null)}
                              placeholder={t('budgets.openEnded')}
                            />
                          </FormControl>
                          {budgetType === 'envelope' && (
                            <p className="text-xs text-muted-foreground mt-1">{t('budgets.endMonthHelp')}</p>
                          )}
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Danger Zone (edit only) — outside the document card */}
            {isEditing && (
              <div>
                <h3 className="text-xs font-semibold uppercase tracking-wider text-destructive mb-3">
                  {t('budgets.dangerZone')}
                </h3>
                <div className="rounded-lg border border-destructive/30 p-4">
                  <p className="text-sm text-muted-foreground mb-3">
                    {t('budgets.dangerZoneDescription')}
                  </p>
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    onClick={() => setDeleteDialogOpen(true)}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    {t('budgets.deleteBudget')}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </Form>
      </FullscreenFormDialog>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('budgets.confirmDelete')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('budgets.confirmDeleteBudgetDescription', { name: budget?.name ?? '' })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteBudget}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
